/**
 * Every time this candidate was offered, from BOTH places the board records one.
 *
 * A proposed slot lands in one of two stores, and which one depends on whether
 * it happened to be blocked first:
 *
 *  - blocked slots get stamped with the offer (`withOffer` in slotHolds.js);
 *  - a slot the recruiter proposed WITHOUT blocking has no hold to stamp — that
 *    is explicit, intended behaviour — so it exists only as the `offered`
 *    tracking entry.
 *
 * Reading only the first is what made "Book case interview" answer "there is
 * nothing to book" about a time the card was showing one line above (Theresa,
 * Sep 2026).
 */
import type { SlotHold, TrackingDoc } from '../../types/taActionBoard';

/** A time that can actually be booked: an ISO start plus how to render it. */
export interface BookableOffer {
  start: string;
  end: string;
  /** True when a calendar hold is already covering it. */
  held: boolean;
}

/**
 * The times on the candidate's most recent `offered` entry.
 *
 * Most recent only, and stopping at a `withdrawn`: an older proposal that was
 * taken back is not something to book, and the card's own "times proposed" list
 * reads the entries the same way.
 */
export function proposedInTracking(tracking: TrackingDoc | undefined): { start: string }[] {
  const entries = tracking?.entries || [];
  for (let i = entries.length - 1; i >= 0; i--) {
    const e = entries[i];
    if (e.kind === 'withdrawn') return [];
    if (e.kind === 'offered') {
      return (e.slotMeta || [])
        .filter((m): m is { label: string; grade: string; start: string } => Boolean(m?.start))
        .map((m) => ({ start: m.start }));
    }
  }
  return [];
}

/**
 * Held offers first (a hold means the time is already protected on the
 * calendars, so it is the safer one to confirm), then anything proposed without
 * one. Deduplicated by start, and chronological within each group.
 *
 * `end` comes from the hold when there is one; a tracking-only slot has no end
 * recorded, so the caller's booking uses the panel's own duration — which is
 * what `bookCaseInterview` does with the start anyway.
 */
export function bookableOffers(
  holds: SlotHold[],
  tracking: TrackingDoc | undefined,
  opportunityId: string,
  panelMinutes = 60
): BookableOffer[] {
  const byStart = new Map<string, BookableOffer>();
  for (const h of holds) {
    if (h.offeredTo?.opportunityId !== opportunityId) continue;
    byStart.set(h.start, { start: h.start, end: h.end, held: true });
  }
  for (const { start } of proposedInTracking(tracking)) {
    if (byStart.has(start)) continue;
    const ms = Date.parse(start);
    if (!Number.isFinite(ms)) continue;
    byStart.set(start, {
      start,
      end: new Date(ms + panelMinutes * 60_000).toISOString(),
      held: false,
    });
  }
  return [...byStart.values()].sort(
    (a, b) => Number(b.held) - Number(a.held) || Date.parse(a.start) - Date.parse(b.start)
  );
}
